import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Building2 } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { format } from "date-fns";
import type { Organization } from "@/types/organization";

interface OrganizationTileProps {
  organization: Organization;
  onEdit?: (organization: Organization) => void;
}

export function OrganizationTile({ organization, onEdit }: OrganizationTileProps) {
  const { t } = useLingui();

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-(--color-border) bg-(--color-surface-muted) p-4 w-full">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="size-14 shrink-0 rounded-[10px] border border-(--color-border) bg-(--color-surface-content) overflow-hidden flex items-center justify-center">
            <Building2 className="size-6 text-(--color-text-secondary)/40" />
          </div>

          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xl font-medium text-(--color-text-primary) leading-snug truncate">
                {organization.name}
              </span>
              <Badge
                variant={organization.isActive ? "success" : "secondary"}
                className={
                  organization.isActive
                    ? "rounded-full border-transparent bg-(--color-status-active) text-(--brand-white) hover:bg-(--color-status-active)/90 shrink-0"
                    : "rounded-full shrink-0"
                }
              >
                {organization.isActive ? t`Active` : t`Inactive`}
              </Badge>
            </div>

            <div className="flex items-center gap-2 text-sm whitespace-nowrap">
              <span className="text-(--color-text-secondary)">{t`Organization ID:`}</span>
              <span className="text-(--color-text-primary)">{organization.slug}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            className="size-11 flex items-center justify-center rounded-[10px] border border-(--color-border) bg-(--color-surface-content) hover:bg-(--color-surface-accent) transition-colors"
          >
            <MoreHorizontal className="size-5 text-(--color-text-secondary)" />
          </button>
          <Button
            className="h-11 rounded-[10px] px-4 text-sm bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg)"
            onClick={() => onEdit?.(organization)}
          >
            {t`Edit Organization`}
          </Button>
        </div>
      </div>

      <div className="border-t border-(--color-border)" />

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-(--color-text-secondary)">
          <span>
            <span className="font-medium text-(--color-text-primary)">
              {organization._count?.companies ?? 0}
            </span>{" "}
            {t`concepts`}
          </span>
          <span className="text-(--gray-300)">•</span>
          <span>
            <span className="font-medium text-(--color-text-primary)">
              {organization._count?.users ?? 0}
            </span>{" "}
            {t`users`}
          </span>
          <span className="text-(--gray-300)">•</span>
          <span>
            <span className="font-medium text-(--color-text-primary)">
              {organization._count?.regions ?? 0}
            </span>{" "}
            {t`regions`}
          </span>
        </div>

        <div className="flex items-center gap-2 text-(--color-text-secondary) whitespace-nowrap">
          <span>{t`Created:`}</span>
          <span className="text-(--color-text-primary)">
            {format(new Date(organization.createdAt), "dd-MM-yyyy")}
          </span>
          <span className="text-(--gray-300)">•</span>
          <span>{t`Updated:`}</span>
          <span className="text-(--color-text-primary)">
            {format(new Date(organization.updatedAt), "dd-MM-yyyy")}
          </span>
        </div>
      </div>
    </div>
  );
}
